import axiosInstance from '../../api/axiosInstance';
import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Trophy, PlusCircle, Trash2, CalendarDays, BarChart3, CheckCircle2 } from 'lucide-react';

function GameResult() {
  const [games, setGames] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameResult, setGameResult] = useState({
    name: '', date: new Date().toISOString().split('T')[0], result: '', gameId: '', code: ''
  });
  const [results, setResults] = useState(null);
  const [monthlyResults, setMonthlyResults] = useState(null);

  useEffect(() => {
    getGames();
    getGameResults(gameResult.date);
    getResultByMonth();
  }, []);

  useEffect(() => {
    if (gameResult.date) getGameResults(gameResult.date);
  }, [gameResult.date]);

  const getGames = async () => {
    try {
      const result = await axiosInstance.get('/getgames');
      if (result?.data) setGames(result.data);
      else setGames([]);
    } catch { toast.error('Error fetching games'); }
  };

  const getGameResults = async (date) => {
    try {
      const result = await axiosInstance.get('/getgameresults', { params: { date } });
      if (result?.data) setResults(result.data);
    } catch { toast.error('Error fetching game results'); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGameResult(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/addgameresult', gameResult);
      toast.success('Result published successfully');
      getGameResults(gameResult.date);
      getResultByMonth();
      setGameResult(prev => ({ ...prev, name: '', result: '', gameId: '', code: '' }));
    } catch { toast.error('Error adding result'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this result?')) return;
    try {
      await axiosInstance.delete('/deletegameresult', { data: { id } });
      toast.success('Result deleted');
      getGameResults(gameResult.date);
      getResultByMonth();
    } catch { toast.error('Error deleting result'); }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const setGameId = (item) => {
    setGameResult(prev => ({ ...prev, gameId: item._id, code: item.code }));
  };

  const resultsByDate = monthlyResults?.reduce((acc, curr) => {
    const date = formatDate(curr.date);
    if (!acc[date]) acc[date] = {};
    acc[date][curr.gameId] = curr.result;
    return acc;
  }, {}) || {};

  const dates = Object.keys(resultsByDate).sort().reverse();

  const getResultByMonth = async () => {
    try {
      const currentDate = new Date();
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const result = await axiosInstance.get('/getgameresultsbymonth', {
        params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      });
      if (result?.data) setMonthlyResults(result.data);
    } catch { toast.error('Error fetching monthly results'); }
  };

  const sortedGames = games ? [...games].sort((a, b) => a.showNumber - b.showNumber) : [];

  return (
    <>
      <div className="page-header">
        <h2>Game Results</h2>
        <p>Publish and manage daily Satta King results.</p>
      </div>

      {/* â”€â”€ Publish Result Card â”€â”€ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title"><PlusCircle size={16} /> Publish Result</div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-grid form-grid-cols-3">
              <div className="form-group">
                <label className="form-label">Select Game</label>
                <select
                  name="name"
                  value={gameResult.name}
                  onChange={(e) => {
                    handleChange(e);
                    const selectedGame = games?.find(g => g.name === e.target.value);
                    if (selectedGame) setGameId(selectedGame);
                  }}
                  className="form-select"
                  required
                >
                  <option value="">-- Select Game --</option>
                  {sortedGames.map((item) => (
                    <option key={item._id} value={item.name}>{item.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  name="date"
                  type="date"
                  value={gameResult.date}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Result Number (00â€“99)</label>
                <input
                  name="result"
                  type="text"
                  value={gameResult.result}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g. 47"
                  maxLength={2}
                  required
                />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button type="submit" className="btn btn-success" disabled={loading}>
                <CheckCircle2 size={15} />
                {loading ? 'Publishing...' : 'Publish Result'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* â”€â”€ Today's Results Table â”€â”€ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title">
            <CalendarDays size={16} /> Results for {gameResult.date}
          </div>
          <span className="badge badge-blue">{results?.length || 0} published</span>
        </div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Game Name</th>
                <th>Date</th>
                <th>Result</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!results || results.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0' }}>No results for this date</td></tr>
              ) : (
                results.map((item) => (
                  <tr key={item._id}>
                    <td style={{ fontWeight: 600 }}>{item.name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{formatDate(item.date)}</td>
                    <td>
                      <span style={{
                        fontSize: '1.2rem', fontWeight: 800,
                        background: 'linear-gradient(135deg, #6c63ff, #3b82f6)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                      }}>{item.result}</span>
                    </td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item._id)}>
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* â”€â”€ Monthly Chart â”€â”€ */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><BarChart3 size={16} /> Monthly Chart” {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
        </div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                {sortedGames.map((game) => (
                  <th key={game._id}>{game.code}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.length === 0 ? (
                <tr><td colSpan={sortedGames.length + 1} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0' }}>No monthly data</td></tr>
              ) : (
                dates.map((date) => (
                  <tr key={date}>
                    <td style={{ fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{date}</td>
                    {sortedGames.map((game) => (
                      <td key={game._id} style={{ textAlign: 'center', fontWeight: 700 }}>
                        {resultsByDate[date]?.[game._id] ? (
                          <span className="badge badge-green">{resultsByDate[date][game._id]}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>â€”</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default GameResult;
